// src/support/SupportChatWidget.js
// Floating chat widget — drop into any ecosystem app with:
//   <SupportChatWidget account={walletAddress} />
// Handles FAQ auto-replies, escalation to support tickets, and full ticket thread.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { processMessage, getGreeting, MSG_USER, MSG_BOT } from './chatEngine';
import { createTicket, addComment, fetchUserTickets, fetchTicketDetail } from './supportApi';
import { isSupportEnabled } from './supportConfig';
import './SupportChatWidget.css';

const VIEW_CHAT = 'chat';
const VIEW_FORM = 'form';
const VIEW_TICKET = 'ticket';

export default function SupportChatWidget({ account, position = 'bottom-right' }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [view, setView] = useState(VIEW_CHAT);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);

  // Ticket form
  const [formSubject, setFormSubject] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPriority, setFormPriority] = useState('normal');

  // Active ticket thread
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketComments, setTicketComments] = useState([]);
  const [ticketReply, setTicketReply] = useState('');

  const endRef = useRef(null);
  const inputRef = useRef(null);

  // If support disabled, render nothing
  if (!isSupportEnabled()) return null;

  // Scroll to bottom on new messages
  const scrollBottom = () => {
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  // Init greeting on first open
  const handleOpen = () => {
    setOpen(true);
    setUnread(0);
    if (messages.length === 0) {
      setMessages(getGreeting());
    }
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Add messages and scroll
  const addMessages = (msgs) => {
    setMessages((prev) => [...prev, ...msgs]);
    if (!open) setUnread((u) => u + msgs.filter((m) => m.type === MSG_BOT).length);
    scrollBottom();
  };

  // Send user message
  const handleSend = () => {
    const text = input.trim();
    if (!text) return;

    const userMsg = { type: MSG_USER, text, ts: Date.now() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    scrollBottom();

    // Process with chat engine
    setTimeout(() => {
      const responses = processMessage(text, newMessages);
      const botMsgs = responses.map((r) => ({ ...r, ts: Date.now() }));
      addMessages(botMsgs);

      // If escalation triggered, switch to form
      if (responses.some((r) => r.escalate)) {
        setTimeout(() => {
          setView(VIEW_FORM);
          // Pre-fill subject from conversation context
          const lastUserMsgs = newMessages.filter((m) => m.type === MSG_USER);
          if (lastUserMsgs.length > 0) {
            setFormSubject(lastUserMsgs[lastUserMsgs.length - 1].text.slice(0, 100));
          }
        }, 800);
      }
    }, 400);
  };

  // Handle suggestion click
  const handleSuggestion = (text) => {
    if (text === 'Create a ticket' || text === 'None of these — create a ticket') {
      setView(VIEW_FORM);
      return;
    }
    if (text === "Yes, thanks!") {
      addMessages([{ type: MSG_BOT, text: "Glad I could help! Let me know if you need anything else.", ts: Date.now() }]);
      return;
    }
    if (text === 'I have another question') {
      addMessages([{ type: MSG_BOT, text: "Sure! What's your question?", ts: Date.now() }]);
      return;
    }
    // Treat as user message
    setInput(text);
    setTimeout(() => {
      const userMsg = { type: MSG_USER, text, ts: Date.now() };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      setInput('');
      scrollBottom();

      setTimeout(() => {
        const responses = processMessage(text, newMessages);
        addMessages(responses.map((r) => ({ ...r, ts: Date.now() })));
        if (responses.some((r) => r.escalate)) {
          setTimeout(() => setView(VIEW_FORM), 800);
        }
      }, 400);
    }, 50);
  };

  // Submit ticket form
  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!formSubject.trim() || !formCategory || !formDesc.trim()) return;
    setSending(true);
    try {
      const result = await createTicket({
        userAddress: account,
        subject: formSubject.trim(),
        category: formCategory,
        description: formDesc.trim(),
        priority: formPriority,
      });
      const ticketId = result.ticket?.id || result.id;
      addMessages([
        { type: MSG_BOT, text: `Ticket created! (ID: ${ticketId}). Our team will respond shortly. You can track it right here.`, ts: Date.now() },
      ]);
      setView(VIEW_CHAT);
      setFormSubject('');
      setFormCategory('');
      setFormDesc('');
      setFormPriority('normal');

      // Load the ticket thread
      if (ticketId && account) {
        const detail = await fetchTicketDetail(ticketId, account);
        setActiveTicket(detail.ticket);
        setTicketComments(detail.comments || []);
        setView(VIEW_TICKET);
      }
    } catch (err) {
      addMessages([{ type: MSG_BOT, text: `Failed to create ticket: ${err.message}. Please try again.`, ts: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  // Send reply in ticket thread
  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !activeTicket) return;
    setSending(true);
    try {
      await addComment(activeTicket.id, account, ticketReply.trim());
      setTicketReply('');
      const detail = await fetchTicketDetail(activeTicket.id, account);
      setActiveTicket(detail.ticket);
      setTicketComments(detail.comments || []);
    } catch {
      // Silently handle — user will see the message didn't appear
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (view === VIEW_CHAT) handleSend();
      else if (view === VIEW_TICKET) handleTicketReply();
    }
  };

  // ── Render ──

  // Bubble button
  const bubble = (
    <button className={`scw-bubble ${position}`} onClick={handleOpen} aria-label="Open support chat">
      <span className="scw-bubble-icon">💬</span>
      {unread > 0 && <span className="scw-badge">{unread}</span>}
    </button>
  );

  if (!open) return bubble;

  return (
    <div className={`scw-container ${position}`}>
      {/* Header */}
      <div className="scw-header">
        <div className="scw-header-info">
          <span className="scw-header-icon">🎧</span>
          <div>
            <div className="scw-header-title">PGT Support</div>
            <div className="scw-header-status">
              {view === VIEW_TICKET ? `Ticket: ${activeTicket?.subject || ''}` : 'Ask anything or create a ticket'}
            </div>
          </div>
        </div>
        <div className="scw-header-actions">
          {view !== VIEW_CHAT && (
            <button className="scw-header-btn" onClick={() => setView(VIEW_CHAT)} title="Back to chat">
              ←
            </button>
          )}
          <button className="scw-header-btn" onClick={() => setOpen(false)} title="Close">
            ✕
          </button>
        </div>
      </div>

      {/* Chat view */}
      {view === VIEW_CHAT && (
        <div className="scw-body">
          <div className="scw-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`scw-msg scw-msg-${msg.type}`}>
                <div className="scw-msg-text">{msg.text}</div>
                {msg.suggestions && (
                  <div className="scw-suggestions">
                    {msg.suggestions.map((s, j) => (
                      <button key={j} className="scw-suggestion-btn" onClick={() => handleSuggestion(s)}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="scw-input-bar">
            <input
              ref={inputRef}
              className="scw-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
            />
            <button className="scw-send-btn" onClick={handleSend} disabled={!input.trim()}>
              ▶
            </button>
          </div>
        </div>
      )}

      {/* Ticket creation form */}
      {view === VIEW_FORM && (
        <div className="scw-body">
          <form className="scw-form" onSubmit={handleSubmitTicket}>
            <h4 className="scw-form-title">Create Support Ticket</h4>
            <div className="scw-field">
              <label>Subject</label>
              <input value={formSubject} onChange={(e) => setFormSubject(e.target.value)} placeholder="Brief description" required />
            </div>
            <div className="scw-field">
              <label>Category</label>
              <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)} required>
                <option value="">Select...</option>
                <option value="refund">Refund</option>
                <option value="technical">Technical Issue</option>
                <option value="event_inquiry">Event Inquiry</option>
                <option value="account">Account</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="scw-field">
              <label>Description</label>
              <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Describe your issue..." rows={4} required />
            </div>
            <div className="scw-field">
              <label>Priority</label>
              <select value={formPriority} onChange={(e) => setFormPriority(e.target.value)}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <button className="scw-submit-btn" type="submit" disabled={sending || !formSubject.trim() || !formCategory || !formDesc.trim()}>
              {sending ? 'Creating...' : 'Create Ticket'}
            </button>
          </form>
        </div>
      )}

      {/* Ticket thread view */}
      {view === VIEW_TICKET && activeTicket && (
        <div className="scw-body">
          <div className="scw-ticket-header">
            <span className={`scw-ticket-status scw-st-${activeTicket.status}`}>
              {(activeTicket.status || '').replace('_', ' ')}
            </span>
            <span className="scw-ticket-cat">{activeTicket.category?.replace('_', ' ')}</span>
          </div>
          <div className="scw-messages">
            {/* Original description */}
            <div className="scw-msg scw-msg-user">
              <div className="scw-msg-text">{activeTicket.description}</div>
            </div>
            {ticketComments.map((c) => (
              <div key={c.id} className={`scw-msg scw-msg-${c.authorType === 'admin' ? 'bot' : 'user'}`}>
                <div className="scw-msg-label">{c.authorType === 'admin' ? 'Support' : 'You'}</div>
                <div className="scw-msg-text">{c.comment}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          {activeTicket.status !== 'closed' && (
            <div className="scw-input-bar">
              <input
                className="scw-input"
                value={ticketReply}
                onChange={(e) => setTicketReply(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Reply..."
              />
              <button className="scw-send-btn" onClick={handleTicketReply} disabled={!ticketReply.trim() || sending}>
                ▶
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
